import { useEffect, useMemo } from 'react';

import { Stack } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import type { Site } from '@hotjar/site';
import { usePlanRecommendations } from '@hotjar/webapp/domains/revenue/recommendations/hooks/usePlanRecommendations';
import {
  BillingPeriodSwitch,
  ProductUpsellTypeEnum,
  tracking,
  useAnnualBillingEligibility,
  ModalId,
  type Context,
} from '@hotjar/upsell';
import {
  extractSkuFromPlanId,
  getProductAllowance,
  getProductBySku,
} from '@hotjar/webapp/domains/common/product/utils';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import type { BillingPeriod } from '@hotjar/subscription-management';

import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { ModalStyles as S, AskBarStyles } from '../styles';
import { PlanSelectActions } from './PlanSelectActions';
import { PlanSelection } from './PlanSelection';

import { AskBar } from './AskBar';
import { ObserveContextualBar } from './ObserveContextualBar/ObserveContextualBar';
import type { ListNode, LinkedList, Step } from '../utils';
import type {
  SelectedProducts,
  ChangeSelectedProducts,
} from '../../MultiProductWrapper/hooks/useMultiProductsState';

interface ModalProps {
  site: Site;
  stepList: LinkedList<Step>;
  currentStep: ListNode<Step> | null;
  isOwner: boolean;
  canSelfServe: boolean;
  goBack: () => void;
  onSubmit: () => void;
  onCancel: (error?: Error) => void;
  selectedProducts: SelectedProducts;
  changeSelectedProducts: ChangeSelectedProducts;
  billingPeriod: BillingPeriod;
  billingPeriodChanged: boolean;
  setBillingPeriod: (billingPeriod: BillingPeriod) => unknown;
  context: Context;
}

const trackingStepName = 'Plan selection';

export const Modal = ({
  site,
  stepList,
  currentStep,
  goBack,
  onSubmit,
  onCancel,
  isOwner,
  canSelfServe,
  selectedProducts,
  changeSelectedProducts,
  billingPeriod,
  billingPeriodChanged,
  setBillingPeriod,
  context,
}: ModalProps): JSX.Element => {
  const currentStepName = currentStep?.data.step;
  const nextStepName = currentStep?.next?.data.step;
  const currentProductType =
    currentStepName === 'observe' ? ProductType.Analytics : ProductType.Feedback;

  const { isEligible: isAnnualEligible } = useAnnualBillingEligibility(site.account);

  const { isLoading: isLoadingRecommendations, products: recommendedProducts } =
    usePlanRecommendations(site);
  const recommendedProduct = recommendedProducts[currentProductType];

  const { t } = useTranslation('billing', { keyPrefix: 'plan-select-upsell-modal' });
  const coverageProps = useTrafficCoverage(site);
  const { data: productCatalog } = useProductCatalog();

  const currentStepProductType =
    currentStepName === 'observe' ? ProductType.Analytics : ProductType.Feedback;

  const { effectivePlanId } = site;
  const sku = extractSkuFromPlanId(effectivePlanId, currentStepProductType);
  const currentProduct = getProductBySku(sku, productCatalog);

  useEffect(() => {
    // Pre select recommended product when it's available and the user didn't select any plan yet
    if (recommendedProduct && !selectedProducts?.[currentProductType]) {
      changeSelectedProducts(currentProductType, recommendedProduct);
      // if recommended product is not available and is not already selected, pre select the current product
    } else if (!recommendedProduct && currentProduct && !selectedProducts?.[currentProductType]) {
      changeSelectedProducts(currentProductType, currentProduct);
    }
  }, [
    changeSelectedProducts,
    currentProduct,
    currentProductType,
    recommendedProduct,
    selectedProducts,
  ]);

  useEffect(() => {
    if (isLoadingRecommendations || !recommendedProduct) {
      return;
    }

    tracking.upsellFlowStepStarted({
      stepName: `${trackingStepName} (${currentProductType})`,
      account: site.account,
      tierRecommended: recommendedProduct?.tier,
      allowanceRecommended: getProductAllowance(recommendedProduct),
    });
  }, [recommendedProduct, site.account, currentProductType, isLoadingRecommendations]);

  const onContinueClick = () => {
    const selectedProduct = selectedProducts[currentProductType];

    tracking.upsellFlowStepFinished({
      account: site.account,
      stepName: `${trackingStepName} (${currentProductType})`,
      tierSelected: selectedProduct?.tier,
      allowanceSelected: getProductAllowance(selectedProduct),
    });

    onSubmit();
  };

  const checkIfCurrentProductIsSelected = useMemo(() => {
    let isCurrentProductSelected = false;
    if (currentProduct && selectedProducts) {
      isCurrentProductSelected =
        currentProduct.sku === selectedProducts[currentStepProductType]?.sku;
    }
    return isCurrentProductSelected;
  }, [currentProduct, currentStepProductType, selectedProducts]);

  const getText = (type: 'subtitle' | 'title') => {
    let productType = ProductUpsellTypeEnum.observe;

    if (stepList.noOfSteps() > 1) {
      productType = ProductUpsellTypeEnum.multi;
    } else if (currentStepName === 'ask') {
      productType = ProductUpsellTypeEnum.ask;
    }

    return t(type, {
      context: `${productType}-${currentStep?.data.stepNo}`,
      totalSteps: stepList.noOfSteps(),
      currentStep: currentStep?.data.stepNo,
    });
  };

  const getIconName = () => {
    return currentStepName === 'ask' ? 'question' : 'hidden';
  };

  // const featureGate = getFeatureGateByReferrer(referrer);

  // Depending on the referrer triggering the PlanSelectModal, this modal can be triggered in the multi product flow (where both ProductSelectUpsellModal & PlanSelectModal are rendered)
  // or in a contextualized flow (where only PlanSelectModal is rendered with a specific context and plan)
  // isStandalone is true when the referrer points to a FeatureFlag with modalId as ModalId.MultiProductUpsellPlanSelectModal, meaning we want to use the contextualized flow
  const isStandalone = context?.modalId === ModalId.MultiProductPlanSelect;
  // isMultiProductFlow is true when the referrer points to a FeatureFlag with modalId as ModalId.MultiProductUpsell, meaning we want to use the multi product flow
  const isMultiProductFlow = !context || context?.modalId === ModalId.MultiProduct;

  const title =
    isStandalone && context?.modalProps?.header?.title
      ? context.modalProps.header.title
      : getText('title');
  const subtitle =
    isStandalone && context?.modalProps?.header?.subtitle
      ? context.modalProps.header.subtitle
      : getText('subtitle');
  const iconName =
    isStandalone && context?.modalProps?.header?.iconName
      ? context.modalProps.header.iconName
      : getIconName();
  const contextProduct = isStandalone ? context.product : null;
  const observeContextualBarIsVisible =
    (isMultiProductFlow && currentStepName === 'observe') ||
    (isStandalone && contextProduct === ProductType.Analytics);

  const askContextualBarIsVisible =
    (isMultiProductFlow && currentStepName === 'ask') ||
    (isStandalone && contextProduct === ProductType.Feedback);

  return (
    <S.Modal isOpen contentLabel={title} onClose={onCancel}>
      <S.ModalHeader title={title} subtitle={subtitle} iconName={iconName} />

      <S.ModalBody>
        <Stack spacing="x2" direction="column">
          <div>
            {observeContextualBarIsVisible && (
              <ObserveContextualBar
                coverageProps={coverageProps}
                site={site}
                contextualBar={
                  isStandalone && context?.modalProps?.contextualBar
                    ? context.modalProps.contextualBar
                    : null
                }
              />
            )}
            {askContextualBarIsVisible && (
              <AskBar>
                <AskBarStyles.Text textColor="text_default" textType="subtitle1">
                  {t('ask-bar')}
                </AskBarStyles.Text>
              </AskBar>
            )}

            {isAnnualEligible && (
              <BillingPeriodSwitch
                value={billingPeriod}
                onChange={setBillingPeriod}
                isLocked={!billingPeriodChanged && billingPeriod === 'ANNUALLY'}
              />
            )}
          </div>

          <PlanSelection
            site={site}
            productType={currentStepProductType}
            recommendedProduct={recommendedProduct}
            selectedProducts={selectedProducts}
            changeSelectedProducts={changeSelectedProducts}
            currentProduct={currentProduct}
            billingPeriod={billingPeriod}
          />
          <S.PriceLabel textType="caption" textColor="text_light">
            {t('price-disclaimer')}
          </S.PriceLabel>
        </Stack>
      </S.ModalBody>

      <S.ModalFooter>
        <PlanSelectActions
          nextStep={isStandalone ? 'checkout' : nextStepName}
          isOwner={isOwner}
          canClickPrev={!!currentStep?.prev}
          canClickNext={!!currentStep?.next}
          onPrevClick={goBack}
          onNextClick={onContinueClick}
          productType={currentStepProductType}
          isCurrentProductSelected={checkIfCurrentProductIsSelected}
          canSelfServe={canSelfServe}
          isStandalone={isStandalone}
        />
      </S.ModalFooter>
    </S.Modal>
  );
};
